import axios from 'axios';

const apiClient = axios.create({
  baseURL: process.env.REACT_APP_API_URL || '',
  timeout: 10000,
  withCredentials: true,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor: attach current access token
apiClient.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('accessToken');
    if (token) {
      config.headers.Authorization = 'Bearer ' + token;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// Read a cookie by name (csrf_token is httpOnly: false, so JS-accessible)
const getCookie = (name) => {
  const match = document.cookie.match(new RegExp('(?:^|;\\s*)' + name + '=([^;]+)'));
  return match ? decodeURIComponent(match[1]) : null;
};

// Queue for requests that arrive while a refresh is already in flight
let isRefreshing = false;
let failedQueue = [];

const processQueue = (error, token = null) => {
  failedQueue.forEach(({ resolve, reject }) => {
    if (error) reject(error);
    else resolve(token);
  });
  failedQueue = [];
};

// Response interceptor: silently refresh access token on 401
apiClient.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;
    const status = error.response?.status;

    // Only handle 401; skip if already retried, if this IS the refresh call, or if not logged in
    const isRefreshCall = originalRequest.url?.includes('/auth/refresh');
    const hasToken = localStorage.getItem('accessToken');
    if (status !== 401 || originalRequest._retry || isRefreshCall || !hasToken) {
      return Promise.reject(error);
    }

    if (isRefreshing) {
      // Park this request until the ongoing refresh finishes
      return new Promise((resolve, reject) => {
        failedQueue.push({ resolve, reject });
      }).then((token) => {
        originalRequest.headers.Authorization = 'Bearer ' + token;
        return apiClient(originalRequest);
      });
    }

    originalRequest._retry = true;
    isRefreshing = true;

    try {
      const csrfToken = getCookie('csrf_token');
      const res = await apiClient.post('/auth/refresh', undefined, {
        headers: csrfToken ? { 'x-csrf-token': csrfToken } : {}
      });
      const newToken = res.data?.data?.accessToken;
      if (!newToken) throw new Error('Empty refresh response');

      localStorage.setItem('accessToken', newToken);
      apiClient.defaults.headers.common.Authorization = 'Bearer ' + newToken;

      processQueue(null, newToken);
      originalRequest.headers.Authorization = 'Bearer ' + newToken;
      return apiClient(originalRequest);
    } catch (refreshError) {
      processQueue(refreshError, null);
      localStorage.removeItem('accessToken');
      localStorage.removeItem('authUser');
      if (window.location.pathname !== '/login' && window.location.pathname !== '/register') {
        window.location.href = '/login';
      }
      return Promise.reject(refreshError);
    } finally {
      isRefreshing = false;
    }
  }
);

export default apiClient;
